// src/navigation/tabs/Tabs.tsx

import React, { useEffect, useState } from "react";
import TabNavigation from "./components/TabNavigation";
import DateRangeSelector from "./components/DateRangeSelector";
import FilterIconButton from "./components/FilterIconButton";
import i18n from "../../i18n";
import classes from "./Tabs.module.css";
import { FilterModal } from "./filter/Filter";
import { TabItem, Interval } from "./TabsTypes";

/**
 * Generic top‑level tabs component that bundles:
 *  • Route‑driven navigation tabs (via <TabNavigation />)
 *  • Date‑interval selector
 *  • Serial‑filter modal
 *
 * The component is agnostic of how each tab is rendered – it simply forwards
 * the `items` array (with optional `color`) to the TabNavigation child.
 */
interface TabsProps {
  items: TabItem[];
  onSelect: (tabItem: TabItem) => void;
  selectedTab: TabItem | undefined;
  onPeriodChange: (value: any, event: React.SyntheticEvent) => void;
  interval: Interval;
  onIntervalChanged: (interval: Interval) => void;
  startDatePeriodTimestamp: number;
  endDatePeriodTimestamp: number;
  serials: string[];
  selectSerials: (selection: string[]) => void;
  selectedSerials: string[];
  /** Remove side padding around the tabs bar */
  noPadding?: boolean;
  /** Collapse/expand behaviour when a side menu is toggled */
  isMenuCollapsed: boolean;
}

const Tabs: React.FC<TabsProps> = ({
  items,
  onSelect,
  selectedTab,
  onPeriodChange,
  interval,
  onIntervalChanged,
  startDatePeriodTimestamp,
  endDatePeriodTimestamp,
  serials,
  selectSerials,
  selectedSerials,
  noPadding,
  isMenuCollapsed,
}) => {
  // Determine date pattern based on current i18n language
  const currentLanguage = i18n.language;
  const dateFormat = currentLanguage.startsWith("fr") ? "dd/MM/yyyy" : "MM/dd/yyyy";

  // Filter modal state --------------------------------------------------------
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(selectedSerials);

  const closeFilterModal = () => setIsFilterModalOpen(false);
  const applyFilters = () => {
    selectSerials(selectedOptions);
    closeFilterModal();
  };

  // Keep local modal‑selection in sync with external changes (e.g. reset)
  useEffect(() => {
    setSelectedOptions(selectedSerials);
  }, [isFilterModalOpen, selectedSerials]);

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div
      className={`${classes.tabContainer} ${isMenuCollapsed ? classes.tabContainerCollapsed : ""} ${noPadding ? classes.tabContainerNoPadding : ""}`}
    >
      <div className={classes.tabContent}>
        {/* Primary navigation bar */}
        <TabNavigation items={items} selectedTab={selectedTab} onSelect={onSelect} />

        {/* Date range + interval selector */}
        <DateRangeSelector
          dateFormat={dateFormat}
          startDatePeriodTimestamp={startDatePeriodTimestamp}
          endDatePeriodTimestamp={endDatePeriodTimestamp}
          onPeriodChange={onPeriodChange}
          onIntervalChanged={onIntervalChanged}
          
        />
      </div>

      {/* Serial filter button & modal */}
      <div className={classes.filterIconContainer}>
        <FilterIconButton onClick={() => setIsFilterModalOpen(true)} />
        {isFilterModalOpen && (
          <FilterModal
            isOpen={isFilterModalOpen}
            onClose={closeFilterModal}
            options={serials}
            selectedOptions={selectedOptions}
            onChange={setSelectedOptions}
            applyChange={applyFilters}
            selectedSerials={selectedSerials}
          />
        )}
      </div>
    </div>
  );
};

export default Tabs;
